import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { getProfile } from '@/features/profiles/lib/profileQueries';
import { ProfileAvatar } from '@/features/profiles/components/profile-avatar';
import {
  listTimelineEvents,
  type TimelineEventWithExtras,
} from '@/features/timeline/lib/timelineQueries';
import { EventDetail } from '@/features/timeline/components/event-detail';
import { getEventTypeMeta } from '@/features/timeline/lib/eventType';
import { askChat, chatErrorMessage, listChatMessages } from '@/features/chat/lib/chatApi';
import {
  CANONICAL_DISCLAIMER,
  isCanonicalRefusal,
  parseCitations,
} from '@/features/chat/lib/parseCitations';
import { SuggestionChips } from '@/features/chat/components/suggestion-chips';
import type { AIChatMessage, Profile } from '@/types/db';

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      profile: Profile;
      events: TimelineEventWithExtras[];
      messages: AIChatMessage[];
    }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

type PendingMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

export function ProfileChatPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  // Synchronous re-entrancy guard. state-based `sending` flips on the next
  // render, so two fast Enter presses could both slip past it.
  const sendingRef = useRef(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setState({ kind: 'loading' });
      const [profile, events, messages] = await Promise.all([
        getProfile(id),
        listTimelineEvents(id),
        listChatMessages(id),
      ]);
      if (!profile) {
        setState({ kind: 'missing' });
        return;
      }
      setState({ kind: 'ready', profile, events, messages });
    } catch (err) {
      console.error('[chat] load failed', err);
      setState({ kind: 'error', message: "Couldn't load this chat." });
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const allMessages = useMemo(() => {
    if (state.kind !== 'ready') return [] as PendingMessage[];
    const stored: PendingMessage[] = state.messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
    }));
    return [...stored, ...pending];
  }, [state, pending]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [allMessages.length, sending]);

  const eventById = useMemo(() => {
    if (state.kind !== 'ready') return new Map<string, TimelineEventWithExtras>();
    return new Map(state.events.map((e) => [e.id, e]));
  }, [state]);

  const openEvent =
    state.kind === 'ready' && openEventId
      ? eventById.get(openEventId) ?? null
      : null;

  async function send(messageText: string) {
    const trimmed = messageText.trim();
    if (!trimmed || sendingRef.current || state.kind !== 'ready') return;
    sendingRef.current = true;

    setErrorBanner(null);
    setDraft('');
    const userTempId = `temp-user-${Date.now()}`;
    const userMsg: PendingMessage = {
      id: userTempId,
      role: 'user',
      content: trimmed,
    };
    setPending((p) => [...p, userMsg]);
    setSending(true);

    // Build the history payload from persisted + any earlier pending messages.
    const historyRoles: { role: 'user' | 'assistant'; content: string }[] = [
      ...state.messages.map((m) => ({ role: m.role, content: m.content })),
      ...pending.map((m) => ({ role: m.role, content: m.content })),
    ];

    const result = await askChat({
      profileId: state.profile.id,
      message: trimmed,
      history: historyRoles,
    });
    setSending(false);
    sendingRef.current = false;

    if (!result.success) {
      setErrorBanner(chatErrorMessage(result.error));
      // Remove the optimistic user bubble so the user isn't left with a
      // ghost-sent message, and restore their draft so one keystroke retries.
      setPending((p) => p.filter((m) => m.id !== userTempId));
      if (result.error !== 'unauthorized') {
        setDraft(trimmed);
      }
      return;
    }

    // Reload persisted messages so the IDs on screen match the DB rows and the
    // citations link to real stored entries.
    try {
      const fresh = await listChatMessages(state.profile.id);
      setState((s) =>
        s.kind === 'ready' ? { ...s, messages: fresh } : s,
      );
      setPending([]);
    } catch (err) {
      console.error('[chat] reload after send failed', err);
      // Fall back to optimistic append so the user at least sees the reply.
      const assistant: PendingMessage = {
        id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content: result.response,
      };
      setPending((p) => [...p, assistant]);
    }
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void send(draft);
  }

  return (
    <main className="min-h-screen flex flex-col safe-top bg-canvas">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          {state.kind === 'ready' ? (
            <Link
              to={`/profile/${state.profile.id}`}
              aria-label="Back to timeline"
              className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Link>
          ) : (
            <Link
              to="/home"
              aria-label="Back to family"
              className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Link>
          )}
          {state.kind === 'ready' && (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <ProfileAvatar
                name={state.profile.name}
                color={state.profile.avatar_color}
                size="sm"
              />
              <div className="min-w-0">
                <h1 className="font-bold truncate leading-tight">
                  Ask about {state.profile.name}
                </h1>
                <p className="text-xs text-ink-muted">
                  Answers from this profile's records only.
                </p>
              </div>
            </div>
          )}
        </div>
      </header>

      <section className="flex-1 overflow-y-auto px-4 py-4 max-w-md w-full mx-auto">
        {state.kind === 'loading' && (
          <div className="card p-6 h-32 animate-pulse bg-canvas" aria-busy="true" />
        )}

        {state.kind === 'missing' && (
          <div className="card p-6 text-center">
            <p className="font-medium mb-2">Profile not found</p>
            <Link to="/home" className="btn-secondary inline-flex">
              Back to family
            </Link>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="card p-6 text-center">
            <p className="text-flag-block mb-3">{state.message}</p>
            <button type="button" onClick={load} className="btn-secondary">
              Try again
            </button>
          </div>
        )}

        {state.kind === 'ready' && (
          <>
            {allMessages.length === 0 && (
              <div className="card p-5 mb-4">
                <p className="font-medium mb-1">
                  Ask anything from {state.profile.name}'s records.
                </p>
                <p className="text-sm text-ink-muted">
                  Every answer links back to the photo or note it came from.
                </p>
              </div>
            )}

            <ul className="space-y-3" aria-live="polite" aria-relevant="additions">
              {allMessages.map((msg) => (
                <li
                  key={msg.id}
                  className={clsx(
                    'flex',
                    msg.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  <div
                    className={clsx(
                      'max-w-[85%] px-3 py-2 text-sm',
                      msg.role === 'user'
                        ? 'rounded-2xl rounded-br-sm bg-accent-soft text-ink border-l-2 border-accent'
                        : isCanonicalRefusal(msg.content)
                          ? 'rounded-2xl rounded-bl-sm bg-canvas text-ink-muted border border-border italic flex items-start gap-2'
                          : 'rounded-2xl rounded-bl-sm bg-surface text-ink border border-border shadow-card',
                    )}
                  >
                    {msg.role === 'assistant' ? (
                      isCanonicalRefusal(msg.content) ? (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4 shrink-0 mt-0.5 text-ink-subtle"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                          <p className="whitespace-pre-wrap break-words">
                            {msg.content}
                          </p>
                        </>
                      ) : (
                        <AssistantContent
                          content={msg.content}
                          eventById={eventById}
                          onCitationClick={(eventId) => setOpenEventId(eventId)}
                        />
                      )
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                  </div>
                </li>
              ))}
              {sending && (
                <li className="flex justify-start" aria-live="polite">
                  <div
                    className="rounded-card px-3 py-2 bg-surface border border-border inline-flex items-center gap-1"
                    aria-label="Assistant is typing"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-accent animate-typing-dot" style={{ animationDelay: '0ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-accent animate-typing-dot" style={{ animationDelay: '150ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-accent animate-typing-dot" style={{ animationDelay: '300ms' }} />
                  </div>
                </li>
              )}
            </ul>

            {errorBanner && (
              <div
                role="alert"
                className="card mt-4 p-3 border-flag-block/40 bg-red-50 text-sm text-flag-block"
              >
                {errorBanner}
              </div>
            )}

            <div ref={bottomRef} />
          </>
        )}
      </section>

      {state.kind === 'ready' && (
        <footer className="sticky bottom-0 bg-surface border-t border-border safe-bottom">
          <div className="max-w-md mx-auto px-4 pt-3 pb-4">
            {!sending && (
              <SuggestionChips
                profile={state.profile}
                events={state.events}
                disabled={sending}
                onSelect={(text) => {
                  setDraft('');
                  void send(text);
                }}
              />
            )}

            <form onSubmit={onSubmit} className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send(draft);
                  }
                }}
                rows={1}
                placeholder={`Ask about ${state.profile.name}…`}
                className="input flex-1 py-2 resize-none"
                disabled={sending}
              />
              <button
                type="submit"
                className="btn-primary px-5"
                disabled={sending || draft.trim().length === 0}
              >
                Send
              </button>
            </form>

            <p className="mt-2 text-[11px] leading-snug text-ink-muted text-center">
              {CANONICAL_DISCLAIMER}
            </p>
          </div>
        </footer>
      )}

      {openEvent && (
        <EventDetail event={openEvent} onClose={() => setOpenEventId(null)} />
      )}
    </main>
  );
}

interface AssistantContentProps {
  content: string;
  eventById: Map<string, TimelineEventWithExtras>;
  onCitationClick: (eventId: string) => void;
}

function AssistantContent({
  content,
  eventById,
  onCitationClick,
}: AssistantContentProps) {
  const segments = parseCitations(content);
  return (
    <p className="whitespace-pre-wrap break-words">
      {segments.map((seg, i) => {
        if (seg.kind === 'text') {
          return <span key={i}>{seg.text}</span>;
        }
        const event = eventById.get(seg.eventId);
        if (!event) {
          // Defensive: edge function should have stripped these already.
          return null;
        }
        const meta = getEventTypeMeta(event.event_type);
        return (
          <button
            key={i}
            type="button"
            onClick={() => onCitationClick(seg.eventId)}
            className={clsx(
              'inline-flex items-center justify-center mx-0.5 px-1.5 min-w-[22px] h-5 rounded-full text-xs font-semibold align-baseline',
              meta.pillBg,
              meta.pillText,
            )}
            aria-label={`Open ${meta.label.toLowerCase()} source record ${seg.index}`}
          >
            {seg.index}
          </button>
        );
      })}
    </p>
  );
}
